import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { User, LoginRequest } from '../types/auth'
import { authApi } from '../api/auth'
import { NivelAcesso } from '../types/auth'
import { resetApiClientState } from '../api/client'
import { createLogger } from '../utils/logger'

const logger = createLogger('AuthContext')

interface UserContext {
  role: 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO'
  empresa_id?: number | null
  nivel_acesso_efetivo: number
  preferencias?: any
  empresa_nome?: string | null
  empresa_razao_social?: string | null
  empresa_cnpj?: string | null
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  hasPermission: (requiredLevel: NivelAcesso) => boolean
  getUserContext: () => UserContext | null
  isSuperAdminMode: () => boolean
  isAdminMode: () => boolean
  isUserMode: () => boolean
  getEmpresaId: () => number | null
  updateContext: (context: UserContext) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

/** Sincroniza tokens do localStorage para sessionStorage (nova aba herda sessão). */
function ensureTokensInSession(): void {
  if (typeof window === 'undefined') return
  if (sessionStorage.getItem('access_token')) return
  const access = localStorage.getItem('access_token')
  const refresh = localStorage.getItem('refresh_token')
  const user = localStorage.getItem('user')
  if (access && refresh && user) {
    sessionStorage.setItem('access_token', access)
    sessionStorage.setItem('refresh_token', refresh)
    sessionStorage.setItem('user', user)
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  ensureTokensInSession()
  // Inicializar isLoading como false se não há token (evita flickering)
  const initialToken = typeof window !== 'undefined' ? sessionStorage.getItem('access_token') : null
  const initialUser = typeof window !== 'undefined' ? sessionStorage.getItem('user') : null
  const [user, setUser] = useState<User | null>(null)
  // Estado para o contexto do usuário (empresa, papel, etc)
  const [userContextState, setUserContextState] = useState<UserContext | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('user_context')
      try {
        return stored ? JSON.parse(stored) : null
      } catch {
        return null
      }
    }
    return null
  })
  const [isLoading, setIsLoading] = useState(!!(initialToken && initialUser))  // Só true se há token para verificar
  const [isInitialized, setIsInitialized] = useState(false)  // Flag para evitar múltiplas inicializações
  const queryClient = useQueryClient()

  const checkAuth = async () => {
    const token = sessionStorage.getItem('access_token')
    const storedUser = sessionStorage.getItem('user')

    // Se não houver token, garantir que está limpo e não autenticado
    if (!token || !storedUser) {
      // Limpar qualquer dado residual (session e local)
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('refresh_token')
      sessionStorage.removeItem('user')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      setUser(null) // Garantir que user é null
      setIsLoading(false)
      return
    }

    // Tentar validar token, mas NÃO limpar se for erro de rede/CORS
    try {
      const validatedUser = await Promise.race([
        authApi.validate(),
        new Promise<User>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 10000) // Aumentar timeout para 10s em produção
        )
      ])
      // Só setar user se validação for bem-sucedida
      setUser(validatedUser)
      sessionStorage.setItem('user', JSON.stringify(validatedUser))
      setIsLoading(false)
    } catch (error: any) {
      // Verificar se é erro de autenticação (401/403) ou erro de rede/CORS
      const isAuthError = error?.response?.status === 401 || error?.response?.status === 403
      const isNetworkError = !error?.response || error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')
      const isTimeout = error?.message === 'Timeout'

      if (isAuthError) {
        // Apenas limpar se for erro de autenticação real
        logger.warn('Token inválido ou expirado, limpando automaticamente:', error)
        sessionStorage.removeItem('access_token')
        sessionStorage.removeItem('refresh_token')
        sessionStorage.removeItem('user')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        setUser(null)
      } else if (isNetworkError || isTimeout) {
        // Erro de rede/CORS ou timeout - NÃO limpar tokens, mas TAMBÉM NÃO restaurar user automaticamente
        // Isso evita loops quando há problemas temporários de rede
        // Apenas deixar user como null para evitar navegação incorreta
        logger.warn('Erro de rede/timeout ao validar token. Mantendo tokens mas NÃO restaurando user automaticamente:', error)
        setUser(null)
        setIsLoading(false)
      } else {
        // Outro tipo de erro - manter tokens mas não setar user
        logger.warn('Erro ao validar token (não é erro de autenticação):', error)
        // Não restaurar user automaticamente - deixar como null
        setUser(null)
        setIsLoading(false)
      }

      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Verificar autenticação apenas uma vez ao montar o componente
    // Usar flag para evitar múltiplas inicializações
    if (isInitialized) {
      return
    }

    setIsInitialized(true)
    const token = sessionStorage.getItem('access_token')
    const storedUser = sessionStorage.getItem('user')

    // Se não houver token, não precisa verificar - definir loading como false IMEDIATAMENTE
    if (!token || !storedUser) {
      setUser(null)
      setIsLoading(false)
      return
    }

    // Verificar autenticação - NÃO restaurar user do sessionStorage antes de validar
    // Isso evita que o app pense que está autenticado quando o token está inválido
    checkAuth().catch((error) => {
      // Em caso de erro, garantir que loading seja false
      logger.error('Erro ao verificar autenticação:', error)
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user && !userContextState) {
      let role: 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO' = 'USUARIO'
      let nivel_acesso_efetivo = NivelAcesso.USUARIO_PADRAO
      
      if (user.nivel_acesso === NivelAcesso.SUPER_ADMIN) {
        role = 'SUPER_ADMIN'
        nivel_acesso_efetivo = NivelAcesso.SUPER_ADMIN
      } else if (user.nivel_acesso === NivelAcesso.ADMINISTRADOR) {
        role = 'ADMINISTRADOR'
        nivel_acesso_efetivo = NivelAcesso.ADMINISTRADOR
      }
      
      const defaultContext: UserContext = {
        role,
        empresa_id: null,
        nivel_acesso_efetivo,
        preferencias: {},
        empresa_nome: null,
        empresa_razao_social: null,
        empresa_cnpj: null
      }
      
      localStorage.setItem('user_context', JSON.stringify(defaultContext))
      setUserContextState(defaultContext)
      logger.info('Auto-inicializado contexto administrativo global para o usuário: ' + user.email)
    }
  }, [user, userContextState])

  const login = async (credentials: LoginRequest) => {
    logger.debug('AuthContext.login chamado:', credentials.email)
    logger.debug('[STEP 1/6] Iniciando processo de login...')

    // Limpar flag de tokens limpos antes do login
    sessionStorage.removeItem('tokens_cleared')
    logger.debug('[STEP 2/6] Flags limpas, preparando requisição...')

    try {
      logger.debug('[STEP 3/6] Chamando authApi.login()...')
      const response = await authApi.login(credentials)
      logger.debug('[STEP 4/6] Resposta recebida do backend:', {
        hasAccessToken: !!response.access_token,
        hasRefreshToken: !!response.refresh_token,
        hasUser: !!response.user,
        userId: response.user?.id,
        userEmail: response.user?.email
      })

      logger.debug('[STEP 5/6] Salvando tokens e usuário no sessionStorage e localStorage (nova aba)...')
      sessionStorage.setItem('access_token', response.access_token)
      sessionStorage.setItem('refresh_token', response.refresh_token)
      sessionStorage.setItem('user', JSON.stringify(response.user))
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)
      localStorage.setItem('user', JSON.stringify(response.user))

      logger.debug('[STEP 6/6] Atualizando estado do React...')
      setUser(response.user)
      setIsLoading(false)

      // Limpar flag de tokens limpos após login bem-sucedido
      sessionStorage.removeItem('tokens_cleared')
      logger.info('✅ Login concluído com sucesso! User ID:', response.user.id)
    } catch (error: any) {
      logger.error('❌ Erro no login:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
        code: error?.code
      })
      setUser(null)
      setIsLoading(false)
      throw error
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      logger.error('Erro ao fazer logout:', error)
    } finally {
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('refresh_token')
      sessionStorage.removeItem('user')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      localStorage.removeItem('user_context')
      setUserContextState(null)
      // Manter galatea_api_base_url no localStorage (é configuração, não token)
      resetApiClientState()
      queryClient.clear()
      if (typeof window !== 'undefined') {
        window.__setGalateaApiBaseUrl?.(null)
      }
      setUser(null)
    }
  }

  const hasPermission = (requiredLevel: NivelAcesso): boolean => {
    if (!user) return false

    // Verificar se há contexto escolhido
    if (userContextState) {
      try {
        // Se o contexto tem nivel_acesso_efetivo, usar ele
        if (userContextState.nivel_acesso_efetivo !== undefined) {
          return userContextState.nivel_acesso_efetivo <= requiredLevel
        }
      } catch (e) {
        // Se der erro, usar verificação normal
      }
    }

    // Fallback para verificação normal
    return user.nivel_acesso <= requiredLevel
  }

  const getUserContext = (): UserContext | null => {
    return userContextState
  }

  const isSuperAdminMode = (): boolean => {
    const context = getUserContext()
    return context?.role === 'SUPER_ADMIN'
  }

  const isAdminMode = (): boolean => {
    const context = getUserContext()
    return context?.role === 'ADMINISTRADOR'
  }

  const isUserMode = (): boolean => {
    const context = getUserContext()
    return context?.role === 'USUARIO'
  }

  const getEmpresaId = (): number | null => {
    const context = getUserContext()
    return context?.empresa_id || null
  }

  const updateContext = (context: UserContext): void => {
    // Atualizar contexto no localStorage
    localStorage.setItem('user_context', JSON.stringify(context))
    // Atualizar estado para provocar re-render
    setUserContextState(context)
    
    // Invalidar queries que dependem do contexto
    queryClient.invalidateQueries({ queryKey: ['menu'] })
    queryClient.invalidateQueries({ queryKey: ['empresa'] })
    queryClient.invalidateQueries({ queryKey: ['telas'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        checkAuth,
        hasPermission,
        getUserContext,
        isSuperAdminMode,
        isAdminMode,
        isUserMode,
        getEmpresaId,
        updateContext,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}


